import { getAuth, onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where } from "firebase/firestore";
import { Fragment, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import QuizProgress from "./QuizProgress";

const RegistrationProcess = () => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();
  const nPages = 2;

  const [currentPage, setCurrentPage] = useState(1);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userInfo, setUserInfo] = useState({});
  const [disabled, setDisabled] = useState(true);

  const sanitizeString = (value) => (typeof value === "string" ? value.trim() : "");

  const checkIfValid = (value) => {
    const normalizedValue = sanitizeString(value);
    return (
      normalizedValue.length >= 5 && /^[a-zA-ZÁáÉéÍíÓóÚú.ÑñüÜ\s]+$/g.test(normalizedValue)
    );
  };

  const nextPage = () => {
    if (currentPage < nPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const terminar = async () => {
    const user = auth.currentUser;
    const normalizedName = sanitizeString(name);
    const normalizedCompanyName = sanitizeString(companyName);
    const memberName = sanitizeString(userInfo.name) || normalizedName;
    const memberLastName = sanitizeString(userInfo.lastName);
    const memberEmail = sanitizeString(userInfo.email) || sanitizeString(user?.email);
    const memberUid = sanitizeString(userInfo.uid) || sanitizeString(user?.uid);

    console.log("RegistrationProcess.terminar debug", {
      currentPage,
      companyName: normalizedCompanyName,
      name: normalizedName,
      selectedPlan,
      userInfo,
      memberPayload: {
        name: memberName,
        lastName: memberLastName,
        email: memberEmail,
        permission: 4,
        uid: memberUid,
      },
      user: user
        ? {
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
          }
        : null,
    });

    if (!user) {
      alert("No hay un usuario autenticado.");
      navigate("/sign-in");
      return;
    }

    if (!checkIfValid(normalizedName) || !checkIfValid(normalizedCompanyName)) {
      alert("Completa correctamente el nombre y la razón social.");
      return;
    }

    if (selectedPlan !== 1 && selectedPlan !== 2) {
      alert("Selecciona un plan para continuar.");
      return;
    }

    try {
      let companyId = "";
      const companyQuery = query(collection(db, "companies"), where("ownerId", "==", user.uid));
      const querySnapshot = await getDocs(companyQuery);
      const existingCompanyDoc = querySnapshot.docs[0];

      if (existingCompanyDoc) {
        companyId = existingCompanyDoc.id;
        await setDoc(
          doc(db, "companies", companyId),
          {
            companyName: normalizedCompanyName,
            ownerEmail: sanitizeString(user.email),
            ownerName: normalizedName,
            plan: selectedPlan,
          },
          { merge: true }
        );
      } else {
        const companyPayload = {
          companyName: normalizedCompanyName,
          dateCreated: new Date(),
          ownerId: user.uid,
          ownerEmail: sanitizeString(user.email),
          ownerName: normalizedName,
          plan: selectedPlan,
          status: "active",
          members: [
            {
              name: memberName,
              lastName: memberLastName,
              email: memberEmail,
              permission: 4,
              uid: memberUid,
            },
          ],
        };

        const companyRef = await addDoc(collection(db, "companies"), companyPayload);
        companyId = companyRef.id;
        await setDoc(doc(db, "companies", companyId), { companyId }, { merge: true });
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          companyId,
          companyName: normalizedCompanyName,
          completeName: normalizedName,
          registrationComplete: true,
          permission: 4,
        },
        { merge: true }
      );

      navigate("/");
    } catch (error) {
      console.error("RegistrationProcess.terminar error", error);
      alert(error.message || "Ocurrió un error al finalizar el registro.");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/landing");
        return;
      }

      if (!user.emailVerified) {
        return;
      }

      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log("No such document!");
        navigate("/landing");
        return;
      }

      const userData = docSnap.data();
      setName(userData.completeName || "");
      setCompanyName(userData.companyName || "");
      setUserInfo(userData);
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  useEffect(() => {
    if (currentPage === 1) {
      setDisabled(!checkIfValid(name) || !checkIfValid(companyName));
      return;
    }

    if (currentPage === 2) {
      setDisabled(selectedPlan === null);
      return;
    }

    setDisabled(false);
  }, [currentPage, name, companyName, selectedPlan]);

  return (
    <Fragment>
      <QuizProgress currentQuestion={currentPage} totalQuestion={nPages} />

      <div className="mt-5">
        {currentPage === 1 ? (
          <div>
            <h3 className="mb-3">Datos para contrato de prestación de servicios</h3>

            <Form>
              <Row>
                <Col lg={12} md={12} className="mb-3">
                  <Form.Label>Nombre Completo*</Form.Label>
                  <Form.Control
                    type="text"
                    id="name"
                    placeholder="Nombre"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                    required
                  />
                </Col>

                <Col lg={12} md={12} className="mb-3">
                  <Form.Label>
                    Nombre del Negocio* <br />
                    <small>(Razón Social)</small>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="comp"
                    value={companyName}
                    placeholder="EJEMPLO S.A de C.V"
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                    }}
                    required
                  />
                </Col>
              </Row>
            </Form>
          </div>
        ) : (
          <Fragment>
            <h3 className="mb-3">Selecciona tu plan</h3>
            <p>Elige una opción para continuar con el registro.</p>

            <div className="d-flex justify-content-around">
              <Form.Check
                type="radio"
                id="radio-market-radar"
                bsPrefix="d-grid"
                className="mb-2 w-100 me-2"
              >
                <Form.Check.Input
                  onChange={() => setSelectedPlan(1)}
                  checked={selectedPlan === 1}
                  type="radio"
                  className="btn-check"
                  name="registration-plan"
                />
                <Form.Check.Label className="btn btn-outline-light-primary text-dark-primary text-center d-flex flex-column align-items-center">
                  <strong>Market Radar</strong>
                  <small>Plan 1</small>
                </Form.Check.Label>
              </Form.Check>

              <Form.Check
                type="radio"
                id="radio-profit-optimizer"
                bsPrefix="d-grid"
                className="mb-2 w-100 ms-2"
              >
                <Form.Check.Input
                  onChange={() => setSelectedPlan(2)}
                  checked={selectedPlan === 2}
                  type="radio"
                  className="btn-check"
                  name="registration-plan"
                />
                <Form.Check.Label className="btn btn-outline-light-primary text-dark-primary text-center d-flex flex-column align-items-center">
                  <strong>Profit Optimizer</strong>
                  <small>Plan 2</small>
                </Form.Check.Label>
              </Form.Check>
            </div>
          </Fragment>
        )}
      </div>

      <div className="mt-3">
        <div className={`d-flex justify-content-${currentPage > 1 ? "between" : "end"}`}>
          {currentPage > 1 && (
            <Button variant="secondary" className="text-white" onClick={prevPage}>
              <i className="fe fe-arrow-left"></i> Atrás
            </Button>
          )}
          {currentPage === nPages ? (
            <Button
              variant="primary"
              onClick={(e) => {
                e.preventDefault();
                terminar();
              }}
            >
              Finish
            </Button>
          ) : (
            <Button variant="primary" onClick={nextPage} disabled={disabled}>
              Siguiente <i className="fe fe-arrow-right"></i>
            </Button>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default RegistrationProcess;
